import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Plus, 
  Target, 
  TrendingUp, 
  Edit, 
  Trash2, 
  BarChart3,
  Activity,
  Users,
  Award
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useRole } from '@/hooks/useRole';
import { toast } from 'sonner';

interface KPI {
  id: string;
  name: string;
  description: string;
  category: string;
  metric_type: string;
  target_value: number;
  measurement_frequency: string;
  is_active: boolean;
  created_at: string;
}

export default function KPIManagement() {
  const { user } = useAuth();
  const { isAdmin } = useRole();
  const [kpis, setKpis] = useState<KPI[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingKPI, setEditingKPI] = useState<KPI | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'performance',
    metric_type: 'numeric',
    target_value: '',
    measurement_frequency: 'quarterly'
  });

  useEffect(() => {
    if (isAdmin) {
      loadKPIs();
    }
  }, [isAdmin]);

  const loadKPIs = async () => {
    try {
      // Get user's district_id first
      const { data: profile } = await supabase
        .from('profiles')
        .select('district_id')
        .eq('id', user?.id)
        .single();

      let query = supabase
        .from('district_kpis')
        .select('*')
        .order('created_at', { ascending: false });

      if (profile?.district_id) {
        query = query.eq('district_id', profile.district_id);
      }

      const { data, error } = await query;
      if (error) throw error;
      setKpis(data || []);
    } catch (error) {
      console.error('Error loading KPIs:', error);
      toast.error('Failed to load KPIs');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a KPI name');
      return;
    }

    try {
      // Get user's district_id
      const { data: profile } = await supabase
        .from('profiles')
        .select('district_id')
        .eq('id', user?.id)
        .single();

      const kpiData = {
        ...formData,
        target_value: formData.target_value ? parseFloat(formData.target_value) : null,
        district_id: profile?.district_id,
        created_by: user?.id
      };

      if (editingKPI) {
        const { error } = await supabase
          .from('district_kpis')
          .update(kpiData)
          .eq('id', editingKPI.id);
        if (error) throw error;
        toast.success('KPI updated successfully');
      } else {
        const { error } = await supabase
          .from('district_kpis')
          .insert(kpiData);
        if (error) throw error;
        toast.success('KPI created successfully');
      }

      setShowDialog(false);
      setEditingKPI(null);
      setFormData({
        name: '',
        description: '',
        category: 'performance',
        metric_type: 'numeric',
        target_value: '',
        measurement_frequency: 'quarterly'
      });
      loadKPIs();
    } catch (error) {
      console.error('Error saving KPI:', error);
      toast.error('Failed to save KPI');
    }
  };

  const handleEdit = (kpi: KPI) => {
    setEditingKPI(kpi);
    setFormData({
      name: kpi.name,
      description: kpi.description || '',
      category: kpi.category,
      metric_type: kpi.metric_type,
      target_value: kpi.target_value?.toString() || '',
      measurement_frequency: kpi.measurement_frequency
    });
    setShowDialog(true);
  };

  const handleDelete = async (kpi: KPI) => {
    if (!confirm(`Are you sure you want to delete "${kpi.name}"?`)) return;

    try {
      const { error } = await supabase
        .from('district_kpis')
        .delete()
        .eq('id', kpi.id);

      if (error) throw error;
      toast.success('KPI deleted successfully');
      loadKPIs();
    } catch (error) {
      console.error('Error deleting KPI:', error);
      toast.error('Failed to delete KPI');
    }
  };

  const toggleActive = async (kpi: KPI) => {
    try {
      const { error } = await supabase
        .from('district_kpis')
        .update({ is_active: !kpi.is_active })
        .eq('id', kpi.id);

      if (error) throw error;
      toast.success(`KPI ${kpi.is_active ? 'deactivated' : 'activated'}`);
      loadKPIs();
    } catch (error) {
      console.error('Error updating KPI status:', error);
      toast.error('Failed to update KPI status');
    }
  };

  const getCategoryIcon = (category: string) => {
    const icons = {
      performance: Activity,
      satisfaction: Award,
      engagement: Users,
      effectiveness: TrendingUp
    };
    return icons[category as keyof typeof icons] || Target;
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      performance: 'bg-blue-100 text-blue-800',
      satisfaction: 'bg-green-100 text-green-800',
      engagement: 'bg-purple-100 text-purple-800',
      effectiveness: 'bg-orange-100 text-orange-800'
    };
    return colors[category as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getMetricTypeIcon = (type: string) => {
    switch (type) {
      case 'scale': return '📊';
      case 'percentage': return '📈';
      case 'numeric': return '🔢';
      case 'boolean': return '✅';
      default: return '📋';
    }
  };

  if (!isAdmin) {
    return (
      <div className="container mx-auto py-8 px-4">
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">You don't have permission to manage KPIs.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold">KPI Management</h1>
          <p className="text-muted-foreground mt-2">
            Manage district key performance indicators for survey alignment and reporting.
          </p>
        </div>
        
        <Button onClick={() => setShowDialog(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add KPI
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total KPIs</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{kpis.length}</div>
            <p className="text-xs text-muted-foreground">
              {kpis.filter(k => k.is_active).length} active
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Performance KPIs</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {kpis.filter(k => k.category === 'performance').length}
            </div>
            <p className="text-xs text-muted-foreground">Performance metrics</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Satisfaction KPIs</CardTitle>
            <Award className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {kpis.filter(k => k.category === 'satisfaction').length}
            </div>
            <p className="text-xs text-muted-foreground">Satisfaction metrics</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Effectiveness KPIs</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {kpis.filter(k => k.category === 'effectiveness').length}
            </div>
            <p className="text-xs text-muted-foreground">Effectiveness metrics</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            District KPIs
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading KPIs...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {kpis.map((kpi) => {
                const CategoryIcon = getCategoryIcon(kpi.category);
                
                return (
                  <Card key={kpi.id} className={`relative ${!kpi.is_active ? 'opacity-60' : ''}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <CategoryIcon className="h-4 w-4" />
                          <h3 className="font-medium text-sm">{kpi.name}</h3>
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => handleEdit(kpi)}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => handleDelete(kpi)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <p className="text-xs text-muted-foreground">{kpi.description}</p>
                      
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className={getCategoryColor(kpi.category)}>
                          {kpi.category}
                        </Badge>
                        <Badge variant="outline" className="flex items-center gap-1">
                          <span>{getMetricTypeIcon(kpi.metric_type)}</span>
                          {kpi.metric_type}
                        </Badge>
                        {kpi.target_value && (
                          <Badge variant="outline" className="flex items-center gap-1">
                            <TrendingUp className="h-3 w-3" />
                            {kpi.target_value}
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">
                          {kpi.measurement_frequency}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleActive(kpi)}
                          className={kpi.is_active ? '' : 'border-green-500 text-green-600'}
                        >
                          {kpi.is_active ? 'Deactivate' : 'Activate'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
          
          {!loading && kpis.length === 0 && (
            <div className="text-center py-8">
              <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold">No KPIs yet</h3>
              <p className="text-muted-foreground mb-6">
                Create your first district KPI to start measuring performance.
              </p>
              <Button onClick={() => setShowDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First KPI
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingKPI ? 'Edit KPI' : 'Create New KPI'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="name">KPI Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Course Effectiveness Rating"
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe what this KPI measures and how it's calculated"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="performance">Performance</SelectItem>
                    <SelectItem value="satisfaction">Satisfaction</SelectItem>
                    <SelectItem value="engagement">Engagement</SelectItem>
                    <SelectItem value="effectiveness">Effectiveness</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Metric Type</Label>
                <Select 
                  value={formData.metric_type} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, metric_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="numeric">Numeric</SelectItem>
                    <SelectItem value="percentage">Percentage</SelectItem>
                    <SelectItem value="scale">Scale (1-5)</SelectItem>
                    <SelectItem value="boolean">Yes/No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="target">Target Value</Label>
                <Input
                  id="target"
                  type="number"
                  step="0.1"
                  value={formData.target_value}
                  onChange={(e) => setFormData(prev => ({ ...prev, target_value: e.target.value }))}
                  placeholder="e.g., 4.0"
                />
              </div>

              <div>
                <Label>Measurement Frequency</Label>
                <Select 
                  value={formData.measurement_frequency} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, measurement_frequency: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave}>
                {editingKPI ? 'Update KPI' : 'Create KPI'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}